<template>
  <Drawer
    :on-close="closePanel"
    :close-icon-position="closeIconPosition"
    :close-title="I18n.t('editor.modal_close')"
    :class="eraseRestoreDrawerStyles"
    container-padding-class="sm:p-6 scrollable-drawer"
  >
    <template #content>
      <VerticalScrollContentContainer>
        <div class="w-full flex md:gap-6 flex-col md:!px-0 gap-2 !p-4 md:!p-0">
          <div class="bg-shape rounded-lg !p-0 hidden md:flex gap-3 items-center">
            <template v-if="store.isMagicBrushEnabled">
              <div class="rounded-lg h-24 w-24 shrink-0 overflow-hidden">
                <video
                  :src="imgEraseRestore"
                  class="rounded-lg"
                  width="100"
                  height="100"
                  :autoplay="true"
                  :loop="true"
                  :muted="true"
                  :controls="false"
                  webkit-playsinline
                  playsinline
                />
              </div>
              <div class="flex flex-col gap-1 pr-2">
                <p class="text-xs font-bold text-typo-tertiary !m-0">{{ I18n.t("editor.magic_brush") }}</p>
                <p class="text-sm text-typo !m-0">{{ I18n.t("editor.easily_erase_or_restore_anything") }}</p>
              </div>
            </template>

            <div v-else class="h-24 flex flex-col items-center justify-center w-full">
              <p class="text-sm text-typo !m-0">{{ I18n.t("editor.manually_erase_or_restore") }}</p>
            </div>
          </div>

          <div class="flex gap-2">
            <ToggleButton :selected="store.drawSettings.action === DrawAction.Erase" @click="enterEraseMode()">
              <RemoveIconCircleLarge class="hidden md:block" />
              <RemoveIconCircle class="md:hidden" />
              <span>{{ I18n.t("editor.erase") }}</span>
            </ToggleButton>

            <ToggleButton :selected="store.drawSettings.action === DrawAction.Restore" @click="enterRestoreMode()">
              <AddIconCircleLarge class="hidden md:block" />
              <AddIconCircle class="md:hidden" />
              <span>{{ I18n.t("editor.restore") }}</span>
            </ToggleButton>
          </div>

          <div
            class="!pt-2"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
            @touchstart="handleMouseDown"
            @touchend="handleMouseUp"
          >
            <RangeSlider
              v-if="store.isMagicBrushEnabled"
              inline-label
              :label="I18n.t('editor.brush_size')"
              :value="magicBrushSliderPosition"
              @update="updateBrushSize"
            />
            <RangeSlider
              v-else
              inline-label
              :label="I18n.t('editor.brush_size')"
              :value="manualBrushSliderPosition"
              @update="updateBrushSize"
            />
          </div>

          <div class="flex justify-between gap-2">
            <Toggle
              :name="I18n.t('editor.magic_brush')"
              :model-value="store.isMagicBrushEnabled"
              :label="I18n.t('editor.magic_brush')"
              @update:modelValue="updateBrushMode"
            />
            <!-- Removed Erase Button Mobile -->
            <!-- <TextButton variant="secondary" @click="reset">{{ I18n.t("editor.reset") }}</TextButton> -->
          </div>
        </div>

        <div class="flex flex-row gap-2 sm:pt-3 hide-on-tablet" v-if="props.isCanvaIntegrationEnabled">
          <div class="flex flex-col gap-3 w-full !pt-2">
            <HorizontalLine />

            <CanvaCtaNew
              :image="store.selectedImage"
              :persistentStore="props.persistentStore"
              :refreshAllHdLayers="props.refreshAllHdLayers"
              :stage="props.stage"
              :stageHd="props.stageHd"
              :getDataURL="props.getDataURL"
              :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
              :canvaCtaIdentifier="CanvaCTAIdentifier.ERASE_DISTRACTIONS"
              :icon="BrushIcon"
              :label="I18n.t('canva_cta.flows.erase_distractions')"
            />

            <CanvaContentImage
              :image="store.selectedImage"
              :src="cutoutContentImage"
              alt="Erase distractions from your image in Canva"
              :getDataURL="props.getDataURL"
              :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
              :canvaCtaIdentifier="CanvaCTAIdentifier.ERASE_DISTRACTIONS_VISUAL"
              :stage="props.stage"
              :stageHd="props.stageHd"
              :persistentStore="props.persistentStore"
              :refreshAllHdLayers="props.refreshAllHdLayers"
            />
          </div>
        </div>
      </VerticalScrollContentContainer>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, computed, inject } from "vue";

import {
  Drawer,
  RangeSlider,
  Toggle,
  ToggleButton,
  AddIconCircle,
  AddIconCircleLarge,
  RemoveIconCircle,
  RemoveIconCircleLarge,
  BrushIcon,
} from "prism";

import imgErase from "@assets/images/erase.mp4";
import imgRestore from "@assets/images/restore.mp4";

import { useEditorStore } from "@/stores/editor_store";
import { DrawAction } from "@/stores/line";
import { Stage } from "konva/lib/Stage";
import { useBrushCursor } from "@/composables/brush_cursor";
import { I18n as I18nType } from "@/src/i18n";

import { CanvaVariant, CanvaCTAIdentifier } from "@/composables/canva_link_builder";
import CanvaCtaNew from "@/components/canva_cta_new.vue";
import cutoutContentImage from "@assets/images/inline-editor/cutout_panel_content.png";
import CanvaContentImage from "@/components/upload/canva_content_image.vue";

import HorizontalLine from "@/components/upload/editor_components/horizontal_line.vue";
import { PersistentStore } from "@/stores/persistent_store";
import { Image } from "@/modules/internal_api/image";
import { useMonitorSize } from "@/composables/monitor_size";
import { usePhotoEditor } from "@/composables/photo_editor";
import VerticalScrollContentContainer from "@/components/prism/vertical_scroll_content_container.vue";

const I18n = inject<I18nType>("I18n");
const store = useEditorStore();

interface EraseRestoreProps {
  editor: HTMLElement;
  stage: Stage;
  stageHd: Promise<Stage>;
  getDataURL: Function;
  scale: number;
  isCanvaIntegrationEnabled: boolean;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}

const props = defineProps<EraseRestoreProps>();

const { isSmallTablet } = useMonitorSize();

const brushCursor = useBrushCursor({ editor: props.editor, stage: props.stage });

// Manual Brush
const maxBrushSize = 200;
const minBrushSize = 1;
const BRUSH_SIZE_DEFAULT = 10;
const brushSize = ref<number>(store.drawSettings?.brushSize || BRUSH_SIZE_DEFAULT);

// Magic Brush
const maxMagicBrushSize = 200;
const minMagicBrushSize = 10;
const MAGIC_BRUSH_SIZE_DEFAULT = 50;
const magicBrushSize = ref<number>(store.drawSettings?.magicBrushSize || MAGIC_BRUSH_SIZE_DEFAULT);

const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

const closeIconPosition = computed(() => {
  return isNewPhotoEditor.value && !isSmallTablet.value ? "none" : "floating";
});

const eraseRestoreDrawerStyles = computed(() => {
  return {
    eraseRestoreDrawer: !isNewPhotoEditor.value,
  };
});

const manualBrushSliderPosition = computed(() => {
  return ((brushSize.value - minBrushSize) / (maxBrushSize - minBrushSize)) * 100;
});

const magicBrushSliderPosition = computed(() => {
  return ((magicBrushSize.value - minMagicBrushSize) / (maxMagicBrushSize - minMagicBrushSize)) * 100;
});

const handleMouseDown = () => {
  store.isAdjustingBrushSize = true;
  const position = brushCursor.centralCursorPosition();
  if (store.isMagicBrushEnabled) {
    store.magicBrushCursorPosition = position;
  } else {
    store.manualBrushCursorPosition = position;
  }
};

const handleMouseUp = () => {
  store.isAdjustingBrushSize = false;
};

const updateBrushSize = (value: number) => {
  const newBrushSize = brushCursor.updateBrushSize(value);
  if (store.isMagicBrushEnabled) {
    magicBrushSize.value = newBrushSize;
  } else {
    brushSize.value = newBrushSize;
  }
};

const updateBrushMode = (value: boolean) => {
  store.isMagicBrushEnabled = value;
};

const imgEraseRestore = computed(() => (store.drawSettings.action === DrawAction.Erase ? imgErase : imgRestore));

const enterEraseMode = () => {
  store.enterEraseMode();
};

const enterRestoreMode = () => {
  store.enterRestoreMode();
};

const emit = defineEmits<{
  (e: "closePanel"): void;
  (e: "reset"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const reset = () => {
  emit("reset");
};
</script>
<style scoped lang="scss">
.eraseRestoreDrawer {
  @media (min-width: 1024px) {
    width: 24.25rem;
  }
}
</style>
